import { Heading, VStack, Input, Button, Box, Table, Thead, 
    Tbody, Tr, Th, Td, Select, useToast, Checkbox, Text, 
    useColorModeValue,
    IconButton, HStack } from '@chakra-ui/react';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { jwtDecode } from "jwt-decode";
import { format } from 'date-fns';
import { useNavigate } from "react-router-dom";
import { FiEdit, FiCheck, FiX, FiTrash } from "react-icons/fi";
import { Offer, Domain, LiveOfferDetails } from '../../Platform/Common/types';
import { useParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { off } from 'process';

// Define the expected structure of the token payload
interface TokenPayload {
  sub: string;       // Typically the username or user ID
  role_code: string;   // Role ID
  exp?: number;      // Expiration timestamp (optional)
}

type OperationMode = "none" | "resequence" ;


const B2COfferManager = () => {
  const [nonLiveOffers, setNonLiveOffers] = useState<Offer[]>([]);
  
  const [liveOffers, setLiveOffers] = useState<LiveOfferDetails[]>([]);
  
  const navigate = useNavigate();

  const toast = useToast();
  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");

  const [domains, setDomains] = useState<Domain[]>([]);

  const { domain_id } = useParams<{ domain_id: string }>();

   const [operationMode, setOperationMode] = useState<OperationMode>("none");

   const [needsResequence, setNeedsResequence] = useState(false);


  // Fetch enrolled students when tenant ID is set
const fetchNonLiveOffers = useCallback(async () => {
  try {
    const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${b2c_apiurl}/domain/${domain_id}/non-live-offers`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setNonLiveOffers(response.data);
  } catch (error) {
    console.error("Failed to load B2C Offers", error);
  }
}, []);

  // Fetch enrolled students when tenant ID is set
const fetchLiveOffers = useCallback(async () => {
  try {
    const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${b2c_apiurl}/domain/${domain_id}/live-offers`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setLiveOffers(response.data);
  } catch (error) {
    console.error("Failed to load Live B2C Offers", error);
  }
}, []);

const fetchDomains = useCallback(async () => {
  try {
    const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
    const response = await axios.get(`${b2c_apiurl}/domains`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('b2c_token')}`,
      },
    });
    setDomains(response.data);
  } catch (error) {
    console.error("Failed to load B2C Domains", error);
  }
}, []);

useEffect(() => {
  fetchLiveOffers();
  fetchNonLiveOffers();
  fetchDomains();
}, [fetchLiveOffers, fetchNonLiveOffers, fetchDomains]);

useEffect(() => {
  if (needsResequence) {
    handleSequenceCommit();
    setNeedsResequence(false);
  }
}, [liveOffers, needsResequence]);

  const handleDeleteOffer = async (offer: Offer) => {
    // Placeholder function
    if (offer.status != "DRAFT")
    {
          toast({ title: "Only Offers in Draft State can be deleted", status: "error" });
    }
    else{
       try {
         const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
       console.log("Printing Token from Delete" + localStorage.getItem("b2c_token"));
       await axios.delete(apiurl + `/offer/${offer.offer_id}`, {
         headers: {
           Authorization: `Bearer ${localStorage.getItem('b2c_token')}`
         }
       });
       await fetchNonLiveOffers();


      // setNeedsResequence(true);   // trigger resequence
       toast({ title: "B2C Offer deleted successfully", status: "success" });
     } catch (error) {
       console.error("Failed to delete B2C Offer", error);
       toast({ title: "Failed to delete B2C Offer", status: "error" });
     }
    }
  };

  const handleAddNewOffer = () => {
        const offer_id = 0;
    navigate(`/practice/app/b2c-domain/${domain_id}/review-offer/${offer_id}`);
  };


  const handleMoveUp = (index: number) => {
    if (index === 0 || operationMode !== "resequence") return;
    const updated = [...liveOffers];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setLiveOffers(updated);
  };
  const handleMoveDown = (index: number) => {
    if (index === domains.length - 1 || operationMode !== "resequence") return;
    const updated = [...liveOffers];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    setLiveOffers(updated);
  };
      
    // Commit handler:  resequence 
    const handleSequenceCommit = async () => {
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const token = localStorage.getItem("b2c_token");
      try {

          // Resequence: compute new order from
          const updatedSequence = liveOffers.map((dm, i) => ({
            offer_id: dm.offer_id,
            sequence_number: i + 1,
          }));
          console.log("printing offer resequence order",updatedSequence);
          await axios.put(`${apiurl}/domain/${domain_id}/live-offers/reorder`, updatedSequence, {
            headers: { Authorization: `Bearer ${token}` },
          });
          toast({
            title: "New order committed",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
  
        // Refresh data
        await fetchLiveOffers();
        setOperationMode("none");

      } catch (error) {
        toast({
          title: `Failed to ${operationMode} offers`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    const handleReviewOffer = (offer_id: number) => {
      navigate(`/practice/app/b2c-domain/${domain_id}/review-offer/${offer_id}`);
    };

    const handleCancelOperation = () => {
              fetchLiveOffers();
              fetchNonLiveOffers();
            setOperationMode("none");
            }


  return (
    <Box as="main" mx="auto" p={10} >
      <VStack mb={4}>
        <Heading size={"md"}>B2C Offer Manager</Heading>
      </VStack>
      <Button  mb={4} borderRadius={"sm"} fontSize="sm" fontWeight="normal" onClick={handleAddNewOffer} isDisabled={operationMode === "resequence" }>
        + B2C Offer
      </Button>
      <Text mb={2} fontWeight="bold">Published B2C Offers</Text>
      <Box borderWidth={"thin"} borderRadius={"md"} borderColor={"blue.500"} p={2}>
          <Box mt={2}>
                <HStack mt={2}>

                  <Button
                    borderRadius={"sm"} fontSize="sm" fontWeight="normal"
                    onClick={() => {
                      setOperationMode("resequence");
                    }}
                    isDisabled={operationMode === "resequence"}
                  >
                    Resequence Offers
                  </Button>
      
                </HStack>
      
                {(operationMode !== "none") && (
                  <HStack mt={4} spacing={3}>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={handleSequenceCommit}
                >
                  Commit
                </Button>
                  <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
                    Cancel
                  </Button>
                  </HStack>
                )}
            </Box>
      
      <Box mt={4}>
       
        <Table mt={4} borderColor={"gray.300"} borderWidth={1} borderRadius="sm">
          <Thead fontSize="12">
            <Tr bgColor={tableHeaderbg}>
              <Th>ID</Th>
            
              <Th>Offer Name</Th>
              <Th>Sequence</Th>
              <Th>Description</Th>
              <Th>Status</Th>
              <Th>Total Price</Th>
              {/* <Th>Created At</Th>
              <Th>Updated At</Th> */}     
            <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="12">

            {liveOffers.map((offer,index) => (
                <Tr
                key={offer.offer_id}
                >
                <Td>{offer.offer_id}</Td>
                <Td>{offer.name}</Td>
                <Td>{offer.sequence}</Td>
                <Td>{offer.description}</Td>
                <Td>{offer.status}</Td>
                <Td>{offer.total_price} {offer.currency}</Td>

                <Td>
                    {operationMode === "resequence" && (
                    <>
                        <IconButton
                        size="xs"
                        icon={<ChevronUpIcon />}
                        aria-label="Move Up"
                        onClick={e => { e.stopPropagation(); handleMoveUp(index); }}
                        />
                        <IconButton
                        size="xs"
                        icon={<ChevronDownIcon />}
                        aria-label="Move Down"
                        onClick={e => { e.stopPropagation(); handleMoveDown(index); }}
                        />
                    </>
                    )}
                {operationMode === "none" && (
                    <>
                  <Tooltip label="Edit">
                      <Button size="sm" onClick={() => handleReviewOffer(offer.offer_id)} mr={8}>
                      <FiEdit />
                      </Button>
                  </Tooltip>
                    
                    </>
                    )}
                </Td>
                </Tr>
            ))}
            </Tbody>
        </Table>
      </Box>
      </Box>

      <Text mt={4} mb={2} fontWeight="bold">Draft and Archived Offers</Text>
      <Box borderWidth={"thin"} borderRadius={"md"} borderColor={"blue.500"} p={2}>

      
      <Box mt={4}>
       
        <Table mt={4} borderColor={"gray.300"} borderWidth={1} borderRadius="sm">
          <Thead fontSize="12">
            <Tr bgColor={tableHeaderbg}>
              <Th>ID</Th>
            
              <Th>Offer Name</Th>
              <Th>Description</Th>
              <Th>Status</Th>
              <Th>Total Price</Th>
              {/* <Th>Created At</Th>
              <Th>Updated At</Th> */}     
            <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="12">

            {nonLiveOffers.map((offer,index) => (
                <Tr
                key={offer.offer_id}
                >
                <Td>{offer.offer_id}</Td>
                <Td>{offer.name}</Td>
                <Td>{offer.description}</Td>
                <Td>{offer.status}</Td>
                <Td>{offer.total_price} {offer.currency}</Td>

                <Td>
                    {operationMode === "resequence" && (
                    <>
                        <IconButton
                        size="xs"
                        icon={<ChevronUpIcon />}
                        aria-label="Move Up"
                        onClick={e => { e.stopPropagation(); handleMoveUp(index); }}
                        />
                        <IconButton
                        size="xs"
                        icon={<ChevronDownIcon />}
                        aria-label="Move Down"
                        onClick={e => { e.stopPropagation(); handleMoveDown(index); }}
                        />
                    </>
                    )}
                {operationMode === "none" && (
                    <>
                  <Tooltip label="Edit">
                      <Button size="sm" onClick={() => handleReviewOffer(offer.offer_id)} mr={8}>
                      <FiEdit />
                      </Button>
                  </Tooltip>
                    <Tooltip label="Delete">
                    <Button
                            size="sm"
                            colorScheme="red"
                            onClick={() => {
                            if (window.confirm("Are you sure you want to delete this offer?")) {
                                handleDeleteOffer(offer);
                            }
                            }}
                        >
                            <FiTrash />
                        </Button>
                    </Tooltip>
                    
                    </>
                    )}
                </Td>
                </Tr>
            ))}
            </Tbody>
        </Table>
      </Box>
      </Box>
    </Box>
  );
};

export default B2COfferManager;
